import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import './DojoSettingsModal.css';
import SettingsShell from './SettingsShell';
import AiCoachSettings, { ExternalCoachAgentSettings } from './AiCoachSettings';
import AiModelSettings from './AiModelSettings';
import CustomMcpSettings from './CustomMcpSettings';
import StrategyModal from './StrategyModal';
import ModalLoading from './ModalLoading';
import DojoRequirementsModal from './DojoRequirementsModal';
import { useModal } from '../services/ModalService';

const defaultLlm = { preset: 'balanced', overrides: {}, providers: [] };

function DojoSettingsModal({ close, backendService, onDojoChange, onRequirementsUpdated, scrollToSection }) {
  const modalService = useModal();
  const [targetSection, setTargetSection] = useState(scrollToSection === "ai-coach" ? "coach" : scrollToSection);
  const [dojo, setDojo] = useState(null);
  const liveSaveScope = useRef(null);
  liveSaveScope.current = { service: backendService, dojoId: dojo?._id };
  const [scopeInvalid, setScopeInvalid] = useState(false);
  const invalidRef = useRef(false);
  const loadEpoch = useRef(0);
  const [refreshNeeded, setRefreshNeeded] = useState(false);
  const [draftBaseline, setDraftBaseline] = useState(null);
  useEffect(() => {
    const invalidate = () => { invalidRef.current = true; loadEpoch.current++; setScopeInvalid(true); setError('Your account scope changed. Drafts are quarantined; close and reopen.'); };
    return backendService.onAccountScopeChanged?.(invalidate);
  }, [backendService]);
  const [progressChiefOnly, setProgressChiefOnly] = useState(false);
  const [progressPolicyDirty, setProgressPolicyDirty] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const deletingRef = useRef(false);
  const savingRef = useRef(false);
  const dismissingRef = useRef(false);
  const planCloseGuard = useRef(null);
  const planBusyRef = useRef(false);
  const [strategyReady, setStrategyReady] = useState(false);
  const [modelsReady, setModelsReady] = useState(false);
  const markStrategyReady = useCallback(() => setStrategyReady(true), []);
  const markModelsReady = useCallback(() => setModelsReady(true), []);
  const initialReady = strategyReady && modelsReady;
  const [error, setError] = useState('');
  const [identityStatus, setIdentityStatus] = useState('');
  const [dojoName, setDojoName] = useState('');
  const [description, setDescription] = useState('');
  const [branding, setBranding] = useState({ logo: null, hero: null, background: null, accent_color: null });
  const [logoPreview, setLogoPreview] = useState(null);
  const [heroPreview, setHeroPreview] = useState(null);
  const [backgroundPreview, setBackgroundPreview] = useState(null);
  const localPreviewUrls = useRef({ logo: null, hero: null, background: null });
  const uploadSequence = useRef({ logo: 0, hero: 0, background: 0 });
  const [brandingPending, setBrandingPending] = useState({ logo: false, hero: false, background: false });
  const [name, setName] = useState('');
  const [emoji, setEmoji] = useState('🥋');
  const [imageUrl, setImageUrl] = useState(null);
  const [imageThumbnailUrl, setImageThumbnailUrl] = useState(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [intensity, setIntensity] = useState('intense');
  const [intensityCustom, setIntensityCustom] = useState('');
  const [personalityPreset, setPersonalityPreset] = useState('kata_default');
  const [personalityCustom, setPersonalityCustom] = useState('');

  const [llm, setLlm] = useState(defaultLlm);

  const isChief = !scopeInvalid && dojo?.user_membership?.role === 'chief';
  const readOnly = Boolean(dojo) && !isChief;
  const brandingBusy = brandingPending.logo || brandingPending.hero || brandingPending.background;
  const { onBeforeClose } = modalService;
  // ModalService owns one guard per depth. Capture the embedded editor's guard
  // locally so it cannot replace (or be replaced by) the Dojo mutation fence.
  const strategyModalService = useMemo(() => ({
    ...modalService,
    disableClose: () => { planBusyRef.current = true; modalService.disableClose?.(); },
    enableClose: () => { planBusyRef.current = false; modalService.enableClose?.(); },
    onBeforeClose: handler => {
      planCloseGuard.current = handler;
      return () => { if (planCloseGuard.current === handler) planCloseGuard.current = null; };
    },
    clearBeforeClose: () => { planCloseGuard.current = null; },
  }), [modalService]);
  const draft = useMemo(() => ({ identity: { dojoName, description, accent: branding.accent_color, focal: branding.hero?.focal_point }, members: { progressChiefOnly }, coach: { name, emoji, imageUrl, imageFile: Boolean(imageFile), intensity, intensityCustom, personalityPreset, personalityCustom, models: { preset: llm.preset, overrides: llm.overrides } } }), [dojoName, description, branding.accent_color, branding.hero?.focal_point, progressChiefOnly, name, emoji, imageUrl, imageFile, intensity, intensityCustom, personalityPreset, personalityCustom, llm]);
  useEffect(() => { if (initialReady && !draftBaseline) setDraftBaseline(draft); }, [initialReady, draftBaseline, draft]);
  const sectionDirty = id => Boolean(draftBaseline && JSON.stringify(draftBaseline[id]) !== JSON.stringify(draft[id]));
  const dirty = draftBaseline && Object.keys(draft).some(sectionDirty);
  const canDismiss = useCallback(async () => {
    if (savingRef.current || deletingRef.current || dismissingRef.current || planBusyRef.current) return false;
    dismissingRef.current = true;
    try { if (dirty && !window.confirm('Discard unsaved settings drafts? Independently saved actions will not be undone.')) return false; return (await planCloseGuard.current?.()) !== false; }
    finally { dismissingRef.current = false; }
  }, [dirty]);
  useEffect(() => onBeforeClose?.(canDismiss), [onBeforeClose, canDismiss]);
  const closeSettings = async () => {
    if (await canDismiss()) close();
  };

  const load = useCallback(async () => {
    const epoch = ++loadEpoch.current;
    const identity = backendService.authenticatedIdentity;
    try {
      const current = await backendService.getMyDojo();
      if (epoch !== loadEpoch.current || identity !== backendService.authenticatedIdentity) return;
      setDojo(current);
      setProgressChiefOnly(current?.progress_image_chief_only === true);
      setProgressPolicyDirty(false);
      if (!current) return;
      setDojoName(current.name || '');
      setDescription(current.description || '');
      setBranding({
        logo: current.branding?.logo || (current.image_url ? { image_url: current.image_url } : null),
        hero: null,
        background: null,
        accent_color: null,
        ...(current.branding || {}),
      });
      const settings = current.ai_coach_settings || {};
      setName(settings.name || '');
      setEmoji(settings.emoji || '🥋');
      setImageUrl(settings.image_url || null);
      setImageThumbnailUrl(settings.image_thumbnail_url || null);
      setImagePreviewUrl(settings.image_preview_url || null);
      setImageFile(null);
      setIntensity(settings.intensity || 'intense');
      setIntensityCustom(settings.intensity_custom || '');
      setPersonalityPreset(settings.coach_personality?.preset || settings.personality_preset || 'kata_default');
      setPersonalityCustom(settings.coach_personality?.custom || settings.personality_custom || '');
      setLlm({ ...defaultLlm, ...(current.llm_settings || {}), providers: current.user_llm_providers || [] });
    } catch (loadError) {
      if (epoch !== loadEpoch.current) return;
      setError(loadError.response?.data?.error || loadError.message || 'Could not load dojo settings.');
    } finally {
      if (epoch === loadEpoch.current) setIsLoading(false);
    }
  }, [backendService]);

  useEffect(() => { const epochs = loadEpoch; load(); return () => { epochs.current++; }; }, [load]);

  useEffect(() => () => {
    Object.values(localPreviewUrls.current).forEach(url => {
      if (url) URL.revokeObjectURL?.(url);
    });
  }, []);

  useEffect(() => {
    if (typeof backendService.fetchDojoBrandingImage !== 'function') return undefined;
    let cancelled = false;
    const objectUrls = [];
    const hydrate = async (url, setter) => {
      if (!url) return;
      try {
        const objectUrl = await backendService.fetchDojoBrandingImage(url);
        if (cancelled) URL.revokeObjectURL?.(objectUrl);
        else { objectUrls.push(objectUrl); setter(objectUrl); }
      } catch (_) {
        if (!cancelled) setter(null);
      }
    };
    hydrate(branding.logo?.thumbnail_url || branding.logo?.image_url, setLogoPreview);
    hydrate(branding.hero?.preview_url || branding.hero?.image_url, setHeroPreview);
    hydrate(branding.background?.preview_url || branding.background?.image_url, setBackgroundPreview);
    return () => {
      cancelled = true;
      objectUrls.forEach(url => URL.revokeObjectURL?.(url));
    };
  }, [backendService, branding.hero?.image_url, branding.hero?.preview_url, branding.background?.image_url, branding.background?.preview_url, branding.logo?.image_url, branding.logo?.thumbnail_url]);

  const updateLlm = useCallback((settings) => setLlm(settings || defaultLlm), []);

  const clearLocalPreview = (assetType) => {
    const oldUrl = localPreviewUrls.current[assetType];
    if (oldUrl) URL.revokeObjectURL?.(oldUrl);
    localPreviewUrls.current[assetType] = null;
  };

  const uploadBranding = async (assetType, file) => {
    if (readOnly || !file || !dojo) return;
    const sequence = ++uploadSequence.current[assetType];
    setBrandingPending(current => ({ ...current, [assetType]: true }));
    const setPreview = assetType === 'logo' ? setLogoPreview : assetType === 'hero' ? setHeroPreview : setBackgroundPreview;
    clearLocalPreview(assetType);
    if (typeof URL.createObjectURL === 'function') {
      const localUrl = URL.createObjectURL(file);
      localPreviewUrls.current[assetType] = localUrl;
      setPreview(localUrl);
    }
    setIdentityStatus(`Uploading ${assetType}…`);
    setError('');
    try {
      const result = await backendService.uploadDojoBrandingImage(dojo._id, assetType, file);
      if (sequence !== uploadSequence.current[assetType]) return;
      clearLocalPreview(assetType);
      setPreview(null);
      setBranding(current => ({
        ...current,
        [assetType]: result.branding?.[assetType] || current[assetType],
      }));
      setIdentityStatus(`${assetType === 'logo' ? 'Logo' : assetType === 'hero' ? 'Hero image' : 'Background image'} saved`);
      onDojoChange?.();
    } catch (uploadError) {
      if (sequence !== uploadSequence.current[assetType]) return;
      clearLocalPreview(assetType);
      setPreview(null);
      setError(uploadError.response?.data?.error || uploadError.message || `Could not save dojo ${assetType}.`);
      setIdentityStatus('');
    } finally {
      if (sequence === uploadSequence.current[assetType]) {
        setBrandingPending(current => ({ ...current, [assetType]: false }));
      }
    }
  };

  const removeBranding = async (assetType) => {
    if (readOnly || !dojo) return;
    const sequence = ++uploadSequence.current[assetType];
    setBrandingPending(current => ({ ...current, [assetType]: true }));
    setIdentityStatus(`Removing ${assetType}…`);
    setError('');
    try {
      const result = await backendService.removeDojoBrandingImage(dojo._id, assetType);
      if (sequence !== uploadSequence.current[assetType]) return;
      clearLocalPreview(assetType);
      if (assetType === 'logo') setLogoPreview(null);
      else if (assetType === 'hero') setHeroPreview(null);
      else setBackgroundPreview(null);
      setBranding(current => ({ ...current, [assetType]: result.branding?.[assetType] || null }));
      setIdentityStatus(`${assetType === 'logo' ? 'Logo' : assetType === 'hero' ? 'Hero image' : 'Background image'} removed`);
      onDojoChange?.();
    } catch (removeError) {
      if (sequence !== uploadSequence.current[assetType]) return;
      setError(removeError.response?.data?.error || removeError.message || `Could not remove dojo ${assetType}.`);
      setIdentityStatus('');
    } finally {
      if (sequence === uploadSequence.current[assetType]) {
        setBrandingPending(current => ({ ...current, [assetType]: false }));
      }
    }
  };

  const refreshSaved = async () => {
    if (savingRef.current || invalidRef.current) return;
    savingRef.current = true; setIsSaving(true);
    try { await onDojoChange?.(); if (!invalidRef.current) { setRefreshNeeded(false); setError(''); setIdentityStatus('Saved settings refreshed'); } }
    catch (_) { setError("Saved; couldn't refresh. Refresh does not repeat the Dojo write."); }
    finally { savingRef.current = false; setIsSaving(false); }
  };
  const save = async () => {
    if (refreshNeeded || invalidRef.current || !isChief || !dojo || !initialReady || brandingBusy || deletingRef.current || savingRef.current || dismissingRef.current || planBusyRef.current) return;
    savingRef.current = true;
    setIsSaving(true);
    setError('');
    try {
      const identity = backendService.authenticatedIdentity;
      const dojoId = dojo._id;
      const isCurrent = () => {
        if (!invalidRef.current && liveSaveScope.current.service === backendService && liveSaveScope.current.dojoId === dojoId && backendService.authenticatedIdentity === identity) return true;
        invalidRef.current = true; setScopeInvalid(true);
        setError('Your account or Dojo scope changed. Drafts are quarantined; close and reopen.');
        return false;
      };
      const current = await backendService.getMyDojo();
      if (!isCurrent()) return;
      if (!current || current._id !== dojo._id || current.user_membership?.role !== 'chief') {
        invalidRef.current = true; setScopeInvalid(true); throw new Error('Dojo owner or chief role changed. Drafts are retained read-only; close and reopen to review.');
      }
      const brandingUpdates = { accent_color: branding.accent_color || null };
      if (branding.hero?.focal_point && branding.hero?.image_url) {
        brandingUpdates.hero = {
          focal_point: branding.hero.focal_point,
          expected_image_url: branding.hero.image_url,
        };
      }
      const updates = {
        name: dojoName,
        description,
        ...(progressPolicyDirty ? { progress_image_chief_only: progressChiefOnly } : {}),
        branding: brandingUpdates,
        ai_coach_settings: {
          name,
          emoji,
          image_url: imageUrl,
          image_thumbnail_url: imageThumbnailUrl,
          image_preview_url: imagePreviewUrl,
          coach_personality: {
            preset: personalityPreset,
            custom: personalityPreset === 'custom' ? personalityCustom : ''
          },
          intensity,
          intensity_custom: intensity === 'custom' ? intensityCustom : '',
        },
        // Providers persist through the dedicated encrypted endpoints in
        // AiModelSettings; sending them here would store raw API keys.
        llm_settings: { preset: llm.preset, overrides: llm.overrides }
      };
      // The server handles same-plan submissions as an idempotent no-op.
      // Keep the selected value in the request even when it matches the
      // loaded value so native-select/string representation differences
      // cannot hide a real selection change.
      if (imageFile) {
        if (!isCurrent()) return;
        const uploaded = await backendService.uploadDojoCoachImage(dojoId, imageFile);
        if (!isCurrent()) {
          setIdentityStatus('Coach image upload completed independently; its saved result was not applied to this quarantined draft.');
          return;
        }
        if (!uploaded?.image_url) throw new Error('Coach image upload returned no saved image URL');
        updates.ai_coach_settings.image_url = uploaded.image_url;
        updates.ai_coach_settings.image_thumbnail_url = uploaded.image_thumbnail_url || null;
        updates.ai_coach_settings.image_preview_url = uploaded.image_preview_url || null;
        setImageUrl(uploaded.image_url);
        setImageThumbnailUrl(uploaded.image_thumbnail_url || null);
        setImagePreviewUrl(uploaded.image_preview_url || null);
        setImageFile(null);
      }
      if (!isCurrent()) return;
      await backendService.updateDojo(dojoId, updates);
      if (!isCurrent()) return;
      setDraftBaseline(draft); setProgressPolicyDirty(false); setIdentityStatus('Dojo settings saved');
      try { await onDojoChange?.(); } catch (_) { setRefreshNeeded(true); setError("Saved; couldn't refresh. Refresh does not repeat the Dojo write."); return; }
      close();
    } catch (saveError) {
      setError(saveError.response?.data?.error || saveError.message || 'Could not save dojo settings.');
    } finally {
      savingRef.current = false;
      setIsSaving(false);
    }
  };

  const deleteDojo = async () => {
    if (!isChief || !dojo?._id || deletingRef.current || savingRef.current || dismissingRef.current || planBusyRef.current || brandingBusy) return;
    if (!window.confirm('Delete this dojo? All members will be removed and their settings restored.')) return;
    deletingRef.current = true;
    setIsDeleting(true);
    setDeleteError('');
    try {
      await backendService.deleteDojo(dojo._id);
    } catch (deleteFailure) {
      setDeleteError(deleteFailure.response?.data?.error || deleteFailure.message || 'Failed to delete dojo');
      deletingRef.current = false;
      setIsDeleting(false);
      return;
    }
    try {
      onDojoChange?.();
    } finally {
      close();
    }
  };

  const loadingView = <div className="dojo-settings-modal dojo-settings-pending"><ModalLoading text="Loading Dojo Settings..." /></div>;
  if (isLoading) return loadingView;
  if (error && !dojo) return <div className="dojo-settings-modal"><p className="dojo-settings-error">{error}</p></div>;
  if (!dojo) return <div className="dojo-settings-modal"><p className="dojo-settings-error">You are not currently in a dojo.</p></div>;

  return (
    <>
    {!initialReady && loadingView}
    {/* Keep loaders mounted, but exclude the entire unfinished form from layout,
        focus and accessibility. Readiness is initial-only, never mutation busy. */}
    <div className="dojo-settings-modal" hidden={!initialReady}>
      <SettingsShell ready={initialReady} title="Dojo settings" context={`${dojo.name} · ${readOnly ? 'Read only' : 'Chief controls'}`} initialSection={targetSection}
        scrollClassName="dojo-settings-scroll" footerClassName="dojo-settings-actions"
        notice={readOnly && <p className="dojo-settings-readonly-notice">Set by {dojo.name}. Only the chief edits shared settings. Personal consent remains in Preferences.</p>}
        sections={[
{ id: 'identity', dirty: sectionDirty('identity'), label: 'Identity', summary: 'Name, artwork & branding', content: <><p className="settings-note">Uploads and removals save immediately. Text, accent and framing use Save changes. Close does not undo saved media.</p>      <section className="dojo-settings-section dojo-identity-settings">
        <div className="dojo-identity-title"><div><h3>Dojo identity</h3><small>Brand the shared dojo separately from its AI coach.</small></div></div>
        <div className="dojo-identity-preview" style={{ '--dojo-accent': branding.accent_color || '#6366f1' }}>
          <div className="dojo-identity-hero" style={{ backgroundImage: heroPreview ? `linear-gradient(rgba(4,6,15,.34), rgba(4,6,15,.9)), url(${heroPreview})` : undefined, backgroundPosition: `${branding.hero?.focal_point?.x ?? 50}% ${branding.hero?.focal_point?.y ?? 50}%` }}>
            <div className="dojo-identity-logo">{logoPreview ? <img src={logoPreview} alt="Dojo logo preview" /> : <span>{dojoName.trim().slice(0, 2).toUpperCase() || '🥋'}</span>}</div>
            <div><strong>{dojoName || 'Your dojo'}</strong><p>{description || 'Add a description for your members.'}</p></div>
          </div>
        </div>
        <label>Dojo name<input value={dojoName} maxLength={80} disabled={readOnly} onChange={event => setDojoName(event.target.value)} /></label>
        <label>Dojo description<textarea value={description} maxLength={1000} disabled={readOnly} rows={3} onChange={event => setDescription(event.target.value)} /></label>
        <div className="dojo-identity-assets">
          <div className="dojo-identity-asset">
            <label>Logo<small>Square PNG, JPEG, WebP, or GIF · at least 128×128</small><input type="file" accept="image/png,image/jpeg,image/webp,image/gif" disabled={readOnly} onChange={event => uploadBranding('logo', event.target.files?.[0])} /></label>
            {!readOnly && branding.logo && <button type="button" className="dojo-identity-remove" onClick={() => removeBranding('logo')}>Remove logo</button>}
          </div>
          <div className="dojo-identity-asset">
            <label>Hero image<small>Landscape · at least 640×240</small><input type="file" accept="image/png,image/jpeg,image/webp,image/gif" disabled={readOnly} onChange={event => uploadBranding('hero', event.target.files?.[0])} /></label>
            {!readOnly && branding.hero && <button type="button" className="dojo-identity-remove" onClick={() => removeBranding('hero')}>Remove hero</button>}
          </div>
          <div className="dojo-identity-asset">
            <label>Background image<small>Activity feed · at least 640×480; fills screen and may crop</small><input type="file" accept="image/png,image/jpeg,image/webp,image/gif" disabled={readOnly} onChange={event => uploadBranding('background', event.target.files?.[0])} /></label>
            {backgroundPreview && <img className="dojo-identity-background-preview" src={backgroundPreview} alt="Dojo activity feed background preview" />}
            {!readOnly && branding.background && <button type="button" className="dojo-identity-remove" onClick={() => removeBranding('background')}>Remove background image</button>}
          </div>
        </div>
        <label>Accent color<input type="color" value={branding.accent_color || '#6366f1'} disabled={readOnly} onChange={event => setBranding(current => ({ ...current, accent_color: event.target.value }))} /></label>
        {branding.hero && <div className="dojo-focal-controls"><label>Hero focus left/right<input type="range" min="0" max="100" value={branding.hero.focal_point?.x ?? 50} disabled={readOnly} onChange={event => setBranding(current => ({ ...current, hero: { ...current.hero, focal_point: { x: Number(event.target.value), y: current.hero.focal_point?.y ?? 50 } } }))} /></label><label>Hero focus up/down<input type="range" min="0" max="100" value={branding.hero.focal_point?.y ?? 50} disabled={readOnly} onChange={event => setBranding(current => ({ ...current, hero: { ...current.hero, focal_point: { x: current.hero.focal_point?.x ?? 50, y: Number(event.target.value) } } }))} /></label></div>}
        {identityStatus && <p className="dojo-identity-status" role="status">{identityStatus}</p>}
      </section>

</> },
{ id: 'strategy', label: 'Strategy & plan', summary: 'Workout & nutrition direction', content: <>      <section className="dojo-settings-section dojo-settings-strategy-plan">
        <StrategyModal backendService={backendService} modalService={strategyModalService} embedded onInitialReady={markStrategyReady} />
      </section>

</> },
{ id: 'members', dirty: sectionDirty('members'), label: 'Members & rules', summary: 'Requirements & image editing', content: <><p className="settings-note">Requirements save separately. Changing leadership does not remove membership rules; change the saved rule before disabling a required personal setting.</p>      <section className="dojo-settings-section" aria-label="Personal progress image policy">
        <h3>Personal progress images</h3>
        <label><input type="checkbox" checked={progressChiefOnly} disabled={readOnly || isSaving} onChange={event => { setProgressChiefOnly(event.target.checked); setProgressPolicyDirty(true); }} /> Only the dojo chief can edit personal progress images</label>
        <p>When enabled, members cannot upload or remove their picture. The current chief and their authorized Coach can manage it. Members always control sharing independently; this policy does not grant access to private images.</p>
      </section>

      <section className="dojo-settings-section dojo-settings-requirements" aria-label="Membership Requirements">
        <h3>📋 Membership Requirements</h3>
        <DojoRequirementsModal embedded backendService={backendService} dojoId={dojo._id} isChief={isChief} onRequirementsUpdated={onRequirementsUpdated || onDojoChange} />
      </section>

</> },
{ id: 'coach', dirty: sectionDirty('coach'), label: 'Coach', summary: 'Shared behavior & model choices', content: <>      <section className="dojo-settings-section">
        <h3>AI coach</h3>
        <AiCoachSettings name={name} setName={setName} emoji={emoji} imageUrl={imageUrl} imagePreviewUrl={imagePreviewUrl} setEmoji={setEmoji} setImageUrl={(nextImageUrl) => { setImageUrl(nextImageUrl); setImageThumbnailUrl(null); setImagePreviewUrl(null); }} setImageFile={setImageFile} intensity={intensity} intensityCustom={intensityCustom} setIntensity={setIntensity} setIntensityCustom={setIntensityCustom} personalityPreset={personalityPreset} personalityCustom={personalityCustom} setPersonalityPreset={setPersonalityPreset} setPersonalityCustom={setPersonalityCustom} readOnly={readOnly} establishedBy={dojo.name} />
      </section>

      <section className="dojo-settings-section dojo-settings-ai-models"><AiModelSettings backendService={backendService} readOnly={readOnly} establishedBy={dojo.name} onSettingsChange={updateLlm} onInitialReady={markModelsReady} /></section>
</> },
{ id: 'connections', label: 'Connections', summary: 'Tools & external Coach', content: <><p className="settings-note">The authorized Dojo Coach can use members’ connected personal MCP tools subject to existing access checks. Tool side effects do not have per-call approval; removing a connection stops future access, not completed effects.</p><button type="button" className="settings-backlink" onClick={() => setTargetSection({ section: "coach" })}>Model providers → Coach</button>      <section className="dojo-settings-section dojo-settings-ai-models"><CustomMcpSettings backendService={backendService} readOnly={readOnly} establishedBy={dojo.name} scope="dojo" /></section>
      {ExternalCoachAgentSettings && <section className="dojo-settings-section"><ExternalCoachAgentSettings backendService={backendService} readOnly={readOnly} /></section>}

</> },
...(isChief ? [{ id: 'administration', label: 'Administration', summary: 'Chief-only actions', content: <>      <section className="dojo-settings-section dojo-settings-danger-zone" aria-label="Danger zone">
        <div><h3>Danger zone</h3><p>Permanently delete this dojo and remove all members. This is separate from saving settings.</p></div>
        <button type="button" className="dojo-settings-delete" disabled={isDeleting || isSaving || brandingBusy} onClick={deleteDojo}>{isDeleting ? 'Deleting…' : 'Delete Dojo'}</button>
        {deleteError && <p className="dojo-settings-error" role="alert">{deleteError}</p>}
      </section>
</> }] : [])        ]}
        footer={<>
        {error && <p className="dojo-settings-error" role="alert">{error}</p>}
        {dirty && !readOnly && <p role="status">Unsaved changes</p>}
        {refreshNeeded && <button type="button" className="social-btn" disabled={isSaving} onClick={refreshSaved}>Refresh saved settings</button>}
        <button type="button" className="social-btn" onClick={closeSettings} disabled={isSaving || isDeleting}>Close</button>
        {!readOnly && <button type="button" className="social-btn primary" disabled={refreshNeeded || isSaving || brandingBusy || isDeleting} onClick={save}>{isSaving ? 'Saving…' : brandingBusy ? 'Finishing media…' : 'Save changes'}</button>}
</>}
      />
    </div>
    </>
  );
}

export default DojoSettingsModal;
